import type {
  ApiLanguage, CachedLangData, LangPack, LangPackStringValuePlural,
} from '../../api/types';

import readStrings from './readStrings';

// Real Simplified Chinese translations bundled locally, not fetched -- `util/localization/index.
// ts`'s own real-language fetch (`callApi('fetchLangPack'/'fetchLanguage', ...)`) goes through the
// gramjs worker, which `DISABLE_REAL_MTPROTO` never instantiates, so it would otherwise silently
// never resolve. Sourced from the tg-im Android fork's own already-downloaded, already-translated
// `tgim_langpack_zh_hans_base.xml` -- real human translations, not a placeholder or machine
// translation. A small, hand-translated block is appended at the end of `zh-hans.strings` for
// Telegram-Web-only auth-screen keys (`AuthTitle`/`LoginQRTitle`/etc.) that have no equivalent in
// Android's own key set at all -- without it, a fresh visitor's very first screen (the QR-code/
// phone-number login flow) would show almost entirely English despite the language actually being
// set to zh-hans correctly underneath. NEVER put a `/* ... */` comment inside `zh-hans.strings`
// itself: `readStrings()` splits the whole file on bare semicolons, not per-line, so a
// semicolon-free comment merges into whichever real entry follows it into one non-`"`-prefixed
// chunk that gets silently dropped (confirmed by hand -- this exact mistake once ate the very
// first hand-translated key).
const ZH_HANS_LANG_CODE = 'zh-hans';
const ZH_HANS_VERSION = 1;

// Unlike `fallback.strings` (whose own key set was curated to never use an underscore for
// anything other than a plural suffix), the Android fork's resource keys this file is built from
// use underscores freely for their own, unrelated naming purposes (e.g. `StarsNeededTextLink_subs`/
// `StarsNeededTextLink_subadvance12` sit right next to a real, independent `StarsNeededTextLink`
// key -- three distinct strings, not plural forms of one). Grouping on ANY underscore (matching
// `readFallbackStrings.ts`'s own simpler split, safe only because of that curation) corrupts data
// here -- `StarsNeededTextLink` would already be stored as a plain string by the time `_subs` is
// reached, and `LangPackStringValuePlural`'s `subs` key doesn't exist, throwing on write. Only
// group when the suffix is one of the six real CLDR plural categories.
const PLURAL_SUFFIXES = new Set(['zero', 'one', 'two', 'few', 'many', 'other']);

export default async function readZhHansStrings(): Promise<CachedLangData> {
  const file = await import('../../assets/localization/zh-hans.strings?raw');
  return buildZhHansStrings(file.default);
}

export function buildZhHansStrings(fileData: string): CachedLangData {
  const rawStrings = readStrings(fileData);

  const strings: LangPack['strings'] = {};

  Object.entries(rawStrings).forEach(([key, value]) => {
    const underscoreIndex = key.lastIndexOf('_');
    const suffix = underscoreIndex === -1 ? undefined : key.slice(underscoreIndex + 1);

    if (!suffix || !PLURAL_SUFFIXES.has(suffix)) {
      strings[key] = value;
      return;
    }

    const clearKey = key.slice(0, underscoreIndex);
    const existing = strings[clearKey];
    const knownValue = (typeof existing === 'object' ? existing : {}) as LangPackStringValuePlural;
    knownValue[suffix as keyof LangPackStringValuePlural] = value;
    strings[clearKey] = knownValue;
  });

  const langPack: LangPack = {
    langCode: ZH_HANS_LANG_CODE,
    version: ZH_HANS_VERSION,
    strings,
  };

  const stringsCount = Object.keys(strings).length;

  // `pluralCode: 'zh'` lets `Intl.PluralRules('zh')` resolve every count to `'other'` (Chinese has
  // no grammatical plural distinction) without needing a manual rule table, unlike the old
  // localization system's own hand-written `PLURAL_RULES`.
  const language: ApiLanguage = {
    langCode: ZH_HANS_LANG_CODE,
    name: 'Chinese (Simplified)',
    nativeName: '简体中文',
    pluralCode: 'zh',
    stringsCount,
    translatedCount: stringsCount,
    translationsUrl: '',
  };

  return {
    langPack,
    language,
  };
}
